/**
 * Formattazione unica di importi e date (locale italiano).
 *
 * Prima convivevano tre modi diversi di scrivere lo stesso importo:
 * "1234.56" (toFixed), "1.234,56" (toLocaleString) e "1.234,56 €"
 * (Intl.NumberFormat). Lo stesso numero poteva quindi comparire in due forme
 * diverse in due pagine, rendendo difficile il confronto a colpo d'occhio.
 *
 * 29/09/2026 — UNICA FONTE anche per le DATE.
 * Regola di Gianni: OGNI data mostrata nell'app deve essere gg/mm/aaaa
 * ("20/09/2027"). I valori SALVATI restano ISO YYYY-MM-DD: qui si cambia solo
 * come si vedono. Per questo formatDate lavora sulle PARTI DELLA STRINGA e non
 * su `new Date("2027-09-20")`, che in Italia (UTC+2) darebbe 19/09/2027.
 */

const EURO = new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" });
const NUMERO = new Intl.NumberFormat("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const INTERO = new Intl.NumberFormat("it-IT", { maximumFractionDigits: 0 });

/** "1.234,56 €" — "—" se il valore non e' un numero. */
export function formatCurrency(valore) {
  const n = Number(valore);
  if (!Number.isFinite(n)) return "—";
  return EURO.format(n);
}

/** "1.234,56" senza simbolo. */
export function formatNumber(valore) {
  const n = Number(valore);
  if (!Number.isFinite(n)) return "—";
  return NUMERO.format(n);
}

/** "1.234" senza decimali (conteggi). */
export function formatInteger(valore) {
  const n = Number(valore);
  if (!Number.isFinite(n)) return "—";
  return INTERO.format(n);
}

// ---------------------------------------------------------------------------
// DATE
// ---------------------------------------------------------------------------

/** "2027-09-20..." — anno-mese-giorno con eventuale orario dietro. */
const ISO = /^(\d{4})-(\d{1,2})-(\d{1,2})(?!\d)/;
/** "20/09/2027", "20-09-2027", "20.09.2027" — gia' italiana. */
const ITALIANA = /^(\d{1,2})[/.\-](\d{1,2})[/.\-](\d{4})/;

const due = (n) => String(n).padStart(2, "0");

/** True se la terna e' una data di calendario vera (non 45/13/2027). */
function ternaValida(a, m, g) {
  const anno = Number(a);
  const mese = Number(m);
  const giorno = Number(g);
  if (!anno || mese < 1 || mese > 12 || giorno < 1 || giorno > 31) return false;
  const d = new Date(anno, mese - 1, giorno);
  return d.getFullYear() === anno && d.getMonth() === mese - 1 && d.getDate() === giorno;
}

/**
 * "2027-09-20" / "2027-9-2" / "2026-09-29T13:54:43Z" / Date -> "20/09/2027".
 *
 * - lavora sulle PARTI della stringa: nessun salto di fuso orario;
 * - accetta anche una data gia' italiana ("20/09/2027" -> "20/09/2027");
 * - vuoto / nullo / non riconoscibile -> `vuoto` (mai "Invalid Date").
 */
export function formatDate(valore, vuoto = "") {
  if (valore === null || valore === undefined || valore === "") return vuoto;

  if (valore instanceof Date) {
    if (Number.isNaN(valore.getTime())) return vuoto;
    return `${due(valore.getDate())}/${due(valore.getMonth() + 1)}/${valore.getFullYear()}`;
  }

  const s = String(valore).trim();
  if (!s) return vuoto;

  let m = ISO.exec(s);
  if (m) {
    const [, anno, mese, giorno] = m;
    if (!ternaValida(anno, mese, giorno)) return vuoto;
    return `${due(giorno)}/${due(mese)}/${anno}`;
  }

  m = ITALIANA.exec(s);
  if (m) {
    const [, giorno, mese, anno] = m;
    if (!ternaValida(anno, mese, giorno)) return vuoto;
    return `${due(giorno)}/${due(mese)}/${anno}`;
  }

  return vuoto;
}

/**
 * "2026-09-29T13:54:43Z" -> "29/09/2026 15:54" (ora LOCALE: e' un istante).
 * Una data senza orario ("2027-09-20") resta "20/09/2027".
 * Vuoto / nullo / non riconoscibile -> `vuoto`.
 */
export function formatDateTime(valore, vuoto = "") {
  if (valore === null || valore === undefined || valore === "") return vuoto;

  if (typeof valore === "string") {
    const s = valore.trim();
    // Solo data: "2027-09-20".
    if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(s)) return formatDate(s, vuoto);
    // Data + ora "2027-09-20 13:54(:43)" oppure "2027-09-20T13:54(:43)".
    const dt = /^(\d{4})-(\d{1,2})-(\d{1,2})[T ](\d{1,2}):(\d{2})/.exec(s);
    if (dt) {
      const [, a, mo, g, h, mi] = dt;
      if (!ternaValida(a, mo, g)) return vuoto;
      const data = `${due(g)}/${due(mo)}/${a}`;
      // Con un fuso dichiarato (Z / +02:00) l'istante va portato in ora LOCALE;
      // senza fuso l'ora e' gia' quella scritta e non si tocca.
      if (/(Z|[+-]\d{2}:?\d{2})$/.test(s)) {
        const d = new Date(s);
        if (!Number.isNaN(d.getTime())) {
          return `${due(d.getDate())}/${due(d.getMonth() + 1)}/${d.getFullYear()} ` +
                 `${due(d.getHours())}:${due(d.getMinutes())}`;
        }
      }
      return `${data} ${due(h)}:${mi}`;
    }
  }

  const d = valore instanceof Date ? valore : new Date(valore);
  if (Number.isNaN(d.getTime())) return formatDate(valore, vuoto);
  return (
    `${due(d.getDate())}/${due(d.getMonth() + 1)}/${d.getFullYear()}` +
    ` ${due(d.getHours())}:${due(d.getMinutes())}`
  );
}

/**
 * "settembre 2027" — per le INTESTAZIONI di periodo (mese del calendario,
 * titoli "Riepilogo ..."). Non e' una data con il giorno, quindi non esiste una
 * forma gg/mm/aaaa: si usa solo dove il giorno non c'e' e non serve.
 */
export function formatMonthYear(valore, vuoto = "") {
  if (valore === null || valore === undefined || valore === "") return vuoto;
  let d;
  if (valore instanceof Date) {
    d = valore;
  } else {
    const m = ISO.exec(String(valore).trim());
    d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(valore);
  }
  if (Number.isNaN(d.getTime())) return vuoto;
  return d.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
}
